import { NavLink, Outlet, useLocation, matchPath } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';

// Each tab declares which roles can see it. AdminLayout filters before
// rendering. Server still enforces auth on every endpoint — this is just
// to avoid showing tabs that would 403.
const TABS = [
  { to: 'dashboard', label: 'Home',     icon: 'ph-gauge',                 roles: ['admin', 'foreman', 'bookkeeper'] },
  { to: 'live',      label: 'Live',     icon: 'ph-broadcast',             roles: ['admin', 'foreman'] },
  { to: 'pay',       label: 'Pay',      icon: 'ph-money',                 roles: ['admin', 'bookkeeper'] },
  { to: 'reports',   label: 'Reports',  icon: 'ph-chart-bar',             roles: ['admin', 'foreman', 'bookkeeper'] },
  { to: 'jobs',      label: 'Jobs',     icon: 'ph-map-pin',               roles: ['admin', 'foreman'] },
  { to: 'crew',      label: 'Crew',     icon: 'ph-users-three',           roles: ['admin', 'foreman', 'bookkeeper'] },
  { to: 'schedule',  label: 'Schedule', icon: 'ph-calendar',              roles: ['admin', 'foreman'] },
  { to: 'onboarding',label: 'Onboard',  icon: 'ph-user-plus',             roles: ['admin'] },
  { to: 'jha',       label: 'JHA',      icon: 'ph-shield-check',          roles: ['admin'] },
  { to: 'jha-log',   label: 'JHA log',  icon: 'ph-shield-checkered',      roles: ['admin', 'foreman', 'bookkeeper'] },
  { to: 'documents', label: 'Docs',     icon: 'ph-file-text',             roles: ['admin', 'foreman'] },
  { to: 'expenses',  label: 'Expenses', icon: 'ph-receipt',               roles: ['admin', 'bookkeeper', 'foreman'] },
  { to: 'geofence',  label: 'Geofence', icon: 'ph-warning',               roles: ['admin', 'foreman'] },
  { to: 'settings',  label: 'Settings', icon: 'ph-gear',                  roles: ['admin'] },
  { to: 'sessions',  label: 'Sessions', icon: 'ph-devices',               roles: ['admin'] },
  { to: 'audit',     label: 'Audit',    icon: 'ph-list-magnifying-glass', roles: ['admin'] },
];

const LABELS = {
  dashboard:  { title: 'Dashboard',        icon: 'ph-gauge' },
  live:       { title: 'Live operations',  icon: 'ph-broadcast' },
  pay:        { title: 'Pay runs',         icon: 'ph-money' },
  reports:    { title: 'Reports',          icon: 'ph-chart-bar' },
  jobs:       { title: 'Jobs',             icon: 'ph-map-pin' },
  crew:       { title: 'Crew',             icon: 'ph-users-three' },
  schedule:   { title: 'Schedule (week)',  icon: 'ph-calendar' },
  onboarding: { title: 'Onboarding',       icon: 'ph-user-plus' },
  jha:        { title: 'JHA template',     icon: 'ph-shield-check' },
  'jha-log':  { title: 'JHA submissions',  icon: 'ph-shield-checkered' },
  documents:  { title: 'All documents',    icon: 'ph-file-text' },
  expenses:   { title: 'Expense queue',    icon: 'ph-receipt' },
  geofence:   { title: 'Geofence log',     icon: 'ph-warning' },
  settings:   { title: 'Settings',         icon: 'ph-gear' },
  sessions:   { title: 'Active sessions',  icon: 'ph-devices' },
  audit:      { title: 'Audit log',        icon: 'ph-list-magnifying-glass' },
};

export default function AdminLayout() {
  const loc = useLocation();
  const { role } = useAuth();
  const visibleTabs = TABS.filter(t => t.roles.includes(role));
  const match = matchPath('/admin/:tab/*', loc.pathname) || matchPath('/admin/:tab', loc.pathname);
  const tab = match?.params?.tab || (visibleTabs[0]?.to || 'pay');
  const meta = LABELS[tab] || { title: 'Admin', icon: 'ph-wrench' };

  return (
    <div className="admin-shell">
      <div className="tab-bar admin-tab-bar">
        {visibleTabs.map(t => (
          <NavLink
            key={t.to}
            to={t.to}
            end={false}
            className={({ isActive }) => 'tab' + (isActive ? ' active' : '')}
            title={t.label}
          >
            <i className={'ph ' + t.icon} />
            <span className="tab-label">{t.label}</span>
          </NavLink>
        ))}
      </div>
      <div className="panel">
        <h3 className="panel-title">
          <span><i className={'ph ' + meta.icon} /> {meta.title}</span>
        </h3>
        <Outlet />
      </div>
    </div>
  );
}
